// Groups Store
import { clone, some } from 'lodash'
import store from 'customerPath/store'

const group = {
  id: null,
  infos: {
    title: '',
  },
  topics: [],
  files: [],
  members: [],
}

const state = {
  pending: {},
  groups: [],
  publicGroups: [],
  group: clone(group),
  groupInvitations: [],
  groupApplications: [],
  status: null,
  permissions: [],
}

const getters = {
  canDeleteGroup: ({ group }) => group && some(group.permissions, p => p === 'delete'),
  canEditGroup: ({ group }) => group && some(group.permissions, p => p === 'edit'),
  canLeaveGroup: ({ group }) => group && some(group.permissions, p => p === 'leave'),
  canManageTopic: ({ group }) => group && some(group.permissions, p => p === 'manage_topics'),
  canPostContent: ({ group }) => group && some(group.permissions, p => p === 'post_content'),
}

const actions = {
  getGroups({ commit }, type) {
    return new Promise(resolve => {
      commit('SET_PENDING', { pending: true, type: 'getGroups' })
      this.api.items
        .getItems('groups', { type })
      .then(({ data }) => {
        commit('SET_GROUPS', data)
        commit('SET_PENDING', { pending: false, type: 'getGroups' })
        resolve()
      })
    })
  },
  getGroup({ commit }, id) {
    return new Promise((resolve, reject) => {
      commit('SET_PENDING', { pending: true, type: 'getGroup' })
      this.api.items
      .getItem(`groups.${id}`)
      .then(({ data }) => {
        if (data.status && data.status === -1) {
          commit('REQUEST_FAILED', data.status)
          resolve(data.status)
        }

        commit('SET_GROUP', data)
        commit('SET_PENDING', { pending: false, type: 'getGroup' })
        resolve()
      }).catch(({ response }) => {
        store.dispatch('app/setError', response.status)
        reject(response)
      })
    })
  },
  getPublicGroups({ commit }) {
    return new Promise(resolve => {
      commit('SET_PENDING', { pending: true, type: 'getPublicGroups' })
      this.api.items
      .getItems('public_groups')
      .then(({ data }) => {
        commit('SET_PUBLIC_GROUPS', data)
        commit('SET_PENDING', { pending: false, type: 'getPublicGroups' })
        resolve()
      })
    })
  },
  checkGroup({ commit }, id) {
    return new Promise((resolve, reject) => {
      commit('SET_PENDING', { pending: true, type: 'checkGroup' })
      this.api.items
      .getItem(`groups.${id}`)
      .then(({ data }) => {
        if (data.status && data.status === -1) {
          commit('REQUEST_FAILED', data.status)
          resolve(data.status)
        }

        commit('SET_PENDING', { pending: false, type: 'checkGroup' })
        resolve()
      }).catch(({ response }) => {
        store.dispatch('app/setError', response.status)
        reject(response)
      })
    })
  },
  getGroupInvitations({ commit }, type) {
    return new Promise(resolve => {
      commit('SET_PENDING', { pending: true, type: 'getGroupInvitations' })
      this.api.items
      .getItems('groups.invitations', { type })
      .then(({ data }) => {
        commit('SET_GROUP_INVITATIONS', data.results)
        commit('SET_PENDING', { pending: false, type: 'getGroupInvitations' })
        resolve()
      })
    })
  },
  getGroupApplications({ commit }) {
    return new Promise(resolve => {
      commit('SET_PENDING', { pending: true, type: 'getGroupApplications' })
      this.api.items
      .getItems('groups.applications')
      .then(({ data }) => {
        commit('SET_GROUP_APPLICATIONS', data.results)
        commit('SET_PENDING', { pending: false, type: 'getGroupApplications' })
        resolve()
      })
    })
  },
  getGroupMembers({ commit }, { id, options }) {
    return new Promise((resolve, reject) => {
      commit('SET_PENDING', { pending: true, type: 'getGroupMembers' })
      this.api.items
      .getItems(`groups/${id}/users`, options)
      .then(({ data }) => {
        if (data.status && data.status === -1) {
          commit('REQUEST_FAILED', data.status)
        }

        commit('SET_GROUP_MEMBERS', data)
        commit('SET_PENDING', { pending: false, type: 'getGroupMembers' })
        resolve()
      }).catch(({ response }) => {
        store.dispatch('app/setError', response.status)
        reject(response)
      })
    })
  },
  acceptInvitation({ commit }, id) {
    return new Promise(resolve => {
      commit('SET_PENDING', { pending: true, type: 'acceptInvitation' })
      this.api.items
      .editItem(`groups.invitations.${id}`)
      .then(_ => {
        commit('SET_PENDING', { pending: false, type: 'acceptInvitation' })
        resolve()
      })
    })
  },
  declineInvitation({ commit }, id) {
    return new Promise(resolve => {
      commit('SET_PENDING', { pending: true, type: 'declineInvitation' })
      this.api.items
      .deleteItem(`groups.invitations.${id}`)
      .then(_ => {
        commit('SET_PENDING', { pending: false, type: 'declineInvitation' })
        resolve()
      })
    })
  },
  acceptRequest({ commit }, id) {
    return new Promise(resolve => {
      commit('SET_PENDING', { pending: true, type: 'acceptRequest' })
      this.api.items
      .editItem(`groups.applications.${id}`)
      .then(_ => {
        commit('SET_PENDING', { pending: false, type: 'acceptRequest' })
        resolve()
      })
    })
  },
  declineRequest({ commit }, id) {
    return new Promise(resolve => {
      commit('SET_PENDING', { pending: true, type: 'declineRequest' })
      this.api.items
      .deleteItem(`groups.applications.${id}`)
      .then(_ => {
        commit('SET_PENDING', { pending: false, type: 'declineRequest' })
        resolve()
      })
    })
  },
  deleteGroup({ commit }, id) {
    return new Promise(resolve => {
      commit('SET_PENDING', { pending: true, type: 'deleteGroup' })
      this.api.items
      .deleteItem(`groups.${id}`)
      .then(({ data }) => {
        if (data.status && data.status === -1) {
          commit('REQUEST_FAILED', data.status)
        }

        commit('SET_PENDING', { pending: false, type: 'deleteGroup' })
        resolve()
      })
    })
  },
  joinGroup({ commit }, id) {
    return new Promise(resolve => {
      commit('SET_PENDING', { pending: true, type: 'joinGroup' })
      this.api.items
      .createItem(`groups.${id}.join`)
      .then(_ => {
        commit('SET_PENDING', { pending: false, type: 'joinGroup' })
        resolve()
      })
    })
  },
  requestApproval({ commit }, id) {
    return new Promise(resolve => {
      commit('SET_PENDING', { pending: true, type: 'requestApproval' })
      this.api.items
      .createItem(`groups.${id}.request_approval`)
      .then(_ => {
        commit('SET_PENDING', { pending: false, type: 'requestApproval' })
        resolve()
      })
    })
  },
  uploadImage({ commit }, img) {
    return new Promise(resolve => {
      commit('SET_PENDING', { pending: true, type: 'uploadImage' })
      this.api.groups
      .putImage(img.id, img.coordinates)
      .then(({ data }) => {
        commit('SET_PENDING', { pending: false, type: 'uploadImage' })
        resolve(data)
      })
    })
  },
  createGroup({ commit }, payload) {
    return new Promise(resolve => {
      commit('SET_PENDING', { pending: true, type: 'createGroup' })
      this.api.items
      .createItem('groups', payload)
      .then(({ data }) => {
        if (data.status && data.status === -1) {
          commit('REQUEST_FAILED', data.status)
        }

        commit('CREATE_GROUP')
        commit('SET_PENDING', { pending: false, type: 'createGroup' })
        resolve()
      })
    })
  },
  editGroup({ commit }, { id, payload }) {
    return new Promise(resolve => {
      commit('SET_PENDING', { pending: true, type: 'editGroup' })
      this.api.items
      .editItem(`groups.${id}`, payload)
      .then(({ data }) => {
        if (data.status && data.status === -1) {
          commit('REQUEST_FAILED', data.status)
        }

        commit('EDIT_GROUP')
        commit('SET_PENDING', { pending: false, type: 'editGroup' })
        resolve()
      })
    })
  },
  leaveGroup({ commit }, id) {
    return new Promise(resolve => {
      commit('SET_PENDING', { pending: true, type: 'leaveGroup' })
      this.api.items
      .deleteItem(`groups.members.${id}`)
      .then(({ data }) => {
        if (data.status && data.status === -1) {
          commit('REQUEST_FAILED', data.status)
        }

        commit('SET_PENDING', { pending: false, type: 'leaveGroup' })
        resolve()
      })
    })
  },
  clearGroups({ commit }) {
    commit('CLEAR_GROUPS')
  },
  clearGroup({ commit }) {
    commit('CLEAR_GROUP')
  },
  clearGroupMembers({ commit }) {
    commit('CLEAR_GROUP_MEMBERS')
  },
}

const mutations = {
  SET_PENDING: (state, { pending, type = 'pending' }) => {
    // to create the reactivity, so that Vue respects the change
    state.pending = { ...state.pending, [type]: pending }
  },
  CLEAR_GROUPS(state) {
    state.groups = []
  },
  CLEAR_GROUP(state) {
    state.status = null
    state.group = clone(group)
  },
  CLEAR_GROUP_MEMBERS(state) {
    state.group.members = { results: [], pagination: {} }
  },
  CREATE_GROUP() {
    return true
  },
  EDIT_GROUP() {
    return true
  },
  REQUEST_FAILED(state, payload) {
    state.status = payload
  },
  SET_GROUP: (state, payload) => {
    state.group = payload
  },
  SET_GROUP_MEMBERS(state, payload) {
    state.group.members.results.push(...payload.results)
    state.group.members.pagination = payload.pagination
  },
  SET_GROUPS: (state, payload) => {
    state.groups = payload.results
    state.permissions = payload.permissions
  },
  SET_PUBLIC_GROUPS: (state, payload) => {
    state.publicGroups = payload.results
  },
  SET_GROUP_INVITATIONS: (state, payload) => {
    state.groupInvitations = payload
  },
  SET_GROUP_APPLICATIONS: (state, payload) => {
    state.groupApplications = payload
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
